import WSServiceType from "./WSServiceType"
import AuthServiceType from "../../auth/services/AuthServiceType"
import { WSEventSubscriber } from "../models"
import HttpServiceType from "../../http/services/HttpService/HttpServiceType"
import { PONG } from "../constants"
/**
 * @param authService

 */

export default function createWSService(authService: AuthServiceType & HttpServiceType): WSServiceType {
    let queue: Array<any> = [];
    let socket: WebSocket | null = null;

    /**
     * The socket `addEventListeners` last bound to.
     *
     * `runListenerEpic` re-calls `addEventListeners()` on a one-second timer for
     * as long as a record is open, and every call used to attach a FRESH set of
     * four listeners to the same socket — nothing deduplicated them. Five
     * minutes on a record page left 301 `message` listeners on one socket, so a
     * single server frame was JSON-parsed 301 times and every subscriber ran 301
     * times: one `updateDoctorListEvent` became hundreds of refetches, growing
     * by one per second for as long as the doctor stayed on the record.
     *
     * Comparing against the socket rather than a boolean keeps the epic useful:
     * after a reconnect `socket` is a new object, so the listeners are bound
     * again exactly once.
     */
    let listenersBoundTo: WebSocket | null = null;

    const SUBSCRIBERS: { [key: string]: Array<WSEventSubscriber> } = {
        "open": [],
        "close": [],
        "error": [],
    };


    function sendMessage(message: unknown) {
        if (socket === null) {
            console.error("Can't send message. Web Socket is null.");
            return
        } else if (socket.readyState !== WebSocket.OPEN) {
            queue.push(message);
            return
        }

        socket.send(JSON.stringify(message));
    }
    const addEventListeners = () => {
        if (socket === null || listenersBoundTo === socket) return

        listenersBoundTo = socket;

        socket.addEventListener("open", e => {
        
            process.env.NODE_ENV !== "production" && console.log("WS open", e);

            SUBSCRIBERS["open"].forEach(subscriber => subscriber());

            queue.forEach(message => {
                socket!.send(JSON.stringify(message));
            });
            queue = [];
        });

        socket.addEventListener("close", e => {

            process.env.NODE_ENV !== "production" && console.log("WS close", e);
        });

        socket.addEventListener("message", e => {
            if (e.data === PONG) return
            process.env.NODE_ENV !== "production" && console.log("WS message event", e.data);

            /*
             * A malformed frame used to throw straight out of this listener,
             * where nothing catches it — the whole handler died and every later
             * frame on this socket was lost silently.
             */
            let parsed;

            try {
                parsed = JSON.parse(e.data)
            } catch (error) {
                console.error("Unparseable WS frame, ignored", e.data);

                return
            }

            /*
             * `doctorUnassignedWSEvent` used to be handled here by setting
             * `myInitObject.data = "true"` — a plain module-level object with no
             * connection to React or redux. Nothing re-rendered when it changed,
             * only the record page ever cleared it, and three unrelated screens
             * read it and either reloaded the whole page or pushed the doctor
             * back to the dashboard at an arbitrary later moment. It is now an
             * ordinary redux action, dispatched by the epic that registered for
             * this event — see ../store/epics.
             */
            if (parsed && typeof SUBSCRIBERS[parsed.type] !== "undefined") {
                SUBSCRIBERS[parsed.type].forEach(subscriber => subscriber(parsed));
            }
        });

        socket.addEventListener("error", e => {
            process.env.NODE_ENV !== "production" && console.log("WS error", e);
        });

      
    }

    function open(url: string) {
        if (socket === null || socket.readyState === WebSocket.CLOSED) {
            socket = new WebSocket(`${authService.getWebSocketUrl()}/${url}?X-Token=${authService.getToken()}`);
            addEventListeners();
            
        }

        return socket
    }

    function close(code: number = 1000, reason: string = "Normal Closure") {
        if (socket !== null) {
            /*
             * EVERY subscriber list, not just open/close/error.
             *
             * The connection epics re-register their handlers each time they
             * open a socket, so leaving the domain lists behind meant a doctor
             * who visited the dashboard five times had five `updateDoctorListEvent`
             * subscribers and refetched the list five times per server event.
             */
            Object.keys(SUBSCRIBERS).forEach(event => {
                SUBSCRIBERS[event] = []
            });

            // Clear queue
            queue = [];

            // Close
            socket.close(code, reason);
            socket = null;
            listenersBoundTo = null;
        }
    }

    function getSocket() {
        return socket
    }

    function register(subscriber: WSEventSubscriber, events: Array<string>): WSServiceType {
        events.forEach(event => {
            if (typeof SUBSCRIBERS[event] === "undefined") {
                SUBSCRIBERS[event] = [];
            }

            SUBSCRIBERS[event].push(subscriber);
        });

        //  @ts-ignore
        return this
    }

    return {
        open,
        close,
        register,
        getSocket,
        sendMessage,
        addEventListeners,
    }
}
